import { Pos } from '../../types/frontendTypes';
import { TileBasedGameFamilyType } from '../../config/tileGameConfig';
import { KPosition, Place, Shape } from '@envclient/envengine/src/envs/koenigsburg/KTile';

const hexaTileWidth = 260;
const hexaTileHeight = 300;
const hexaTileDX = 130; // offset on spritesheet
const hexaTileDY = 226; // offset on spritesheet

const rhombusTileWidth = 260;
const rhombusTileHeight = 150;

const triangleTileWidth = 130;
const triangleTileHeight = 150;

const triangleCenter = (triangleTileHeight / 2) * Math.tan(30 * (Math.PI / 180)); // 0.5 * triangleTileHeight * tan(30°)

function koenigsburgTileCoordsByIndex(i: number) {
  const hexaMapping: Record<number, Pos> = {
    49: { x: 0, y: 0 },
    50: { x: 1, y: 0 },
    51: { x: 0, y: 1 },
    52: { x: 1, y: 1 },
    53: { x: 0, y: 2 },
    54: { x: 1, y: 2 },
    55: { x: 0, y: 3 }
  };

  if (i < 100) {
    return {
      x: hexaMapping[i].x * hexaTileWidth,
      y: hexaMapping[i].y * hexaTileHeight
    };
  }

  const rhombusMapping: Record<number, Pos> = {
    100: { x: 0, y: 0 },
    101: { x: 1, y: 0 },
    102: { x: 0, y: 1 },
    103: { x: 1, y: 1 },
    104: { x: 0, y: 2 },
    105: { x: 1, y: 2 },
    106: { x: 0, y: 3 },
    107: { x: 1, y: 3 },
    108: { x: 0, y: 4 }
  };

  if (i >= 100 && i < 200) {
    return {
      x: rhombusMapping[i].x * rhombusTileWidth,
      y: rhombusMapping[i].y * rhombusTileHeight
    };
  }

  const triangleMapping: Record<number, Pos> = {
    200: { x: 0, y: 0 },
    201: { x: 0, y: 1 },
    202: { x: 0, y: 2 },
    203: { x: 0, y: 3 }
  };

  if (i >= 200) {
    return {
      x: triangleMapping[i].x * triangleTileWidth,
      y: triangleMapping[i].y * triangleTileHeight
    };
  }
}

function isKoenigsburgFamily(family: TileBasedGameFamilyType): boolean {
  return true;
}

const getTileCoords = (type: number) => {
  return koenigsburgTileCoordsByIndex(type);
};

// calculates background size, offset and rotation
// coordinates are position of the tile in the sprite sheet (tilemap png)
const getContainerStyle = (coordinates: Pos, rotation: number, shape: Shape) => {
  if (shape === 'hexa') {
    return {
      width: hexaTileWidth + 'px',
      height: hexaTileHeight + 'px',
      backgroundPosition: `-${coordinates.x}px -${coordinates.y}px`,
      transformOrigin: hexaTileWidth / 2 + 'px ' + hexaTileHeight / 2 + 'px',
      transform: 'rotate(' + rotation + 'deg)'
    };
  }
  if (shape === 'rhombus') {
    return {
      width: rhombusTileWidth + 'px',
      height: rhombusTileHeight + 'px',
      backgroundPosition: `-${coordinates.x}px -${coordinates.y}px`,
      transformOrigin: rhombusTileWidth / 2 + 'px ' + rhombusTileHeight / 2 + 'px',
      transform: 'rotate(' + rotation + 'deg)'
    };
  }
  // triangle default
  return {
    width: triangleTileWidth + 'px',
    height: triangleTileHeight + 'px',
    backgroundPosition: `-${coordinates.x}px -${coordinates.y}px`,
    transformOrigin: triangleCenter + 'px ' + triangleTileHeight / 2 + 'px',
    transform: 'rotate(' + rotation + 'deg)'
  };
};

const cloneAndOrderPlace = (place: Place) => {
  const p: Place = JSON.parse(JSON.stringify(place));
  p.positions.sort((a, b) => a.slot - b.slot);
  return p;
};

// calculate position of top/left corner of hexa tile on the canvas using tile.place
const getKTileStyle = (originalPlace: Place) => {
  const place = cloneAndOrderPlace(originalPlace);
  //when a rhombus  overlaps 2 hexas - some positions are on another hexa  -so we need to also know the selectedPosition (x,y)
  if (
    place.shape === 'rhombus' &&
    (place.positions[0].x !== place.positions[1].x || place.positions[0].y !== place.positions[1].y)
  ) {
    const { x: x0, y: y0, slot: slot0 } = place.positions[0];
    const { x: x1, y: y1, slot: slot1 } = place.positions[1];
    // 0,3
    if (Math.min(slot0, slot1) === 0) {
      const left = x0 * hexaTileWidth + y0 * hexaTileDX + hexaTileWidth / 2 + 'px';
      const top = y0 * hexaTileDY - hexaTileHeight / 4 + 'px';
      return { left, top };
    }
    // 1,4
    if (Math.min(slot0, slot1) === 1) {
      const left = x0 * hexaTileWidth + y0 * hexaTileDX + hexaTileWidth / 2 + 'px';
      const top = y0 * hexaTileDY + hexaTileHeight / 4 + 'px';
      return { left, top };
    }
    // 2, 5
    if (Math.min(slot0, slot1) === 2) {
      const left = x0 * hexaTileWidth + y0 * hexaTileDX + 'px';
      const top = y0 * hexaTileDY + hexaTileHeight / 2 + 'px';
      return { left, top };
    }
  }
  // hexa overlaps - 2 cases
  // south-east
  if (place.shape === 'hexa' && place.positions[0].y > place.positions[1].y) {
    const { x: x0, y: y0 } = place.positions[0];
    const left = x0 * hexaTileWidth + y0 * hexaTileDX + 'px';
    const top = y0 * hexaTileDY - hexaTileHeight / 2 + 'px';
    return { left, top };
  }
  // east
  if (place.shape === 'hexa' && place.positions[0].x < place.positions[5].x) {
    const { x: x0, y: y0 } = place.positions[0];
    const left = x0 * hexaTileWidth + y0 * hexaTileDX + hexaTileWidth / 2 + 'px';
    const top = y0 * hexaTileDY - hexaTileHeight / 4 + 'px';
    return { left, top };
  }
  // normal cases
  const { x, y } = place.positions[0];
  // x offset in Descart coord depends on x AND y
  const left = x * hexaTileWidth + y * hexaTileDX + 'px';
  const top = y * hexaTileDY + 'px';
  return { left, top };
};

// calculates the pivot rotation for a shape (hexa - 0))
const getPivotStyle = (originalPlace: Place) => {
  const place = cloneAndOrderPlace(originalPlace);
  const { shape, positions } = place;
  if (shape === 'hexa') {
    return {
      width: hexaTileWidth + 'px',
      height: hexaTileHeight + 'px',
      left: '0px',
      top: '0px',
      transform: 'rotate(0deg)'
    };
  } else if (shape === 'rhombus') {
    const max = Math.max(positions[0].slot, positions[1].slot);
    const min = Math.min(positions[0].slot, positions[1].slot);
    let slotRotation = (max === 5 && min === 0 ? 0 : max) * 60;
    // when a rhombus  overlaps 2 hexas - slotRotations are different
    if (
      place.shape === 'rhombus' &&
      (place.positions[0].x !== place.positions[1].x || place.positions[0].y !== place.positions[1].y)
    ) {
      // 0,3 maps to 4,5
      if (min === 0) {
        slotRotation = 5 * 60;
      }
      // 1,4 maps to 0,5
      if (min === 1) {
        slotRotation = 0;
      }
      // 2,5 maps to 0,1
      if (min === 2) {
        slotRotation = 60;
      }
    }
    return {
      left: '0px',
      transform: 'rotate(' + slotRotation + 'deg)',
      transformOrigin: rhombusTileWidth / 2 + 'px ' + rhombusTileHeight + 'px'
    };
  } else if (shape === 'triangle') {
    const slotRotation = positions[0].slot * 60;
    return {
      left: hexaTileWidth / 2 + 'px',
      transform: 'rotate(' + slotRotation + 'deg)',
      transformOrigin: '0px ' + triangleTileHeight + 'px'
    };
  }
};

const getTileWidth = (shape: Shape) => {
  if (shape === 'rhombus') return rhombusTileWidth;
  else if (shape === 'hexa') return hexaTileWidth;
  else return triangleTileWidth;
};

const getTileHeight = (shape: Shape) => {
  if (shape === 'rhombus') return rhombusTileHeight;
  else if (shape === 'hexa') return hexaTileHeight;
  else return triangleTileHeight;
};

export const defaultTrianglePlace: Place = {
  shape: 'triangle',
  positions: [new KPosition().init(0, 0, 0)]
};

export const defaultRhombusPlace: Place = {
  shape: 'rhombus',
  positions: [new KPosition().init(0, 0, 0), new KPosition().init(0, 0, 5)]
};

export const defaultHexaPlace: Place = {
  shape: 'hexa',
  positions: [
    new KPosition().init(0, 0, 0),
    new KPosition().init(0, 0, 1),
    new KPosition().init(0, 0, 2),
    new KPosition().init(0, 0, 3),
    new KPosition().init(0, 0, 4),
    new KPosition().init(0, 0, 5)
  ]
};
export {
  isKoenigsburgFamily,
  getKTileStyle,
  getTileCoords,
  getContainerStyle,
  getTileWidth,
  getTileHeight,
  getPivotStyle
};
